import React, { useEffect, useState } from 'react';
import { FaSyncAlt, FaTimes } from 'react-icons/fa';
import { APP_VERSION } from '../version';
import './updateBanner.css';

// How often an open tab asks whether a different viewer build is being served,
// and the shortest gap between checks when focus or visibility changes fire them.
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const MIN_RECHECK_MS = 60 * 1000;

const BUILD_ID = /^[0-9a-f]{8,64}$/;

/**
 * The id of the build this page is running, from the <meta name="dview-lite-build">
 * tag scripts/write-build-meta.js stamps into index.html. null under `npm start`
 * and in tests, which have no stamped index.html, so nothing is checked there.
 */
export const runningBuildId = () => {
  const tag = document.querySelector('meta[name="dview-lite-build"]');
  const id = tag && tag.getAttribute('content');
  return id && BUILD_ID.test(id) ? id : null;
};

/**
 * The build the server publishes now, from meta.json next to index.html, or null
 * when unknown.
 *
 * The URL is root-relative to the page's own origin, which is where lite is
 * served in every case: nvr.cloud, an NVR reached by IP or hostname, the
 * isolated origin of Monarch's remote HTTP proxy, and an eView jump (a jump
 * only moves the API calls to jumpedServerUrl; the page and its bundle still
 * come from the NVR that served it, so never ask the jumped NVR).
 *
 * Unknown covers a server without the file (404 on an NVR, index.html from the
 * CloudFront or SPA fallback), an expired remote proxy session (a 403 page),
 * an NVR restarting mid-upgrade, and being offline.
 */
export const fetchServedBuild = async () => {
  try {
    // Resolved against the origin, not the current route, so a relative
    // PUBLIC_URL (".") still names the file beside index.html.
    const url = new URL(`${process.env.PUBLIC_URL || ''}/meta.json`, window.location.origin).href;
    const response = await window.fetch(url, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const body = await response.json();
    if (!body || typeof body.build !== 'string' || !BUILD_ID.test(body.build)) return null;
    return { build: body.build, version: typeof body.version === 'string' ? body.version : '' };
  } catch (_) {
    return null;
  }
};

/** "Viewer v2026.10.2 is available." when the version moved, else a generic line ("dev" builds). */
export const updateMessage = (served, runningVersion = APP_VERSION) => (
  served.version && /^\d/.test(served.version) && served.version !== runningVersion
    ? `Viewer v${served.version} is available.`
    : 'A new version of the viewer is available.'
);

/**
 * Offers a reload when the server publishes a different viewer build than this
 * page is running: after a cloud deploy, or after an NVR upgrade replaced the
 * viewer container. It never reloads on its own, because that would drop open
 * live video and playback.
 *
 * Reload is a plain reload. index.html and meta.json are served without caching,
 * and every bundle that carries this banner unregisters the retired CRA service
 * worker on startup (index.js), so no worker answers the reload from a stale
 * precache. A browser still controlled by that worker is running an older bundle
 * without this banner; the update check for docker/files/service-worker-reset.js
 * clears its caches and reloads it instead.
 */
const UpdateBanner = ({ runningBuild = runningBuildId(), fetchBuild = fetchServedBuild }) => {
  const [available, setAvailable] = useState(null);
  const [dismissed, setDismissed] = useState(null);

  useEffect(() => {
    if (!runningBuild) return undefined;
    let active = true;
    let lastCheck = -Infinity;
    let latest = 0;
    const check = async () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastCheck < MIN_RECHECK_MS) return;
      lastCheck = Date.now();
      const request = ++latest;
      const served = await fetchBuild();
      // Only the newest check answers: a slow earlier request must not bring
      // back a banner a later one cleared. Unknown keeps the last answer, so an
      // NVR mid-upgrade or a dropped network neither raises nor clears it.
      if (active && request === latest && served) setAvailable(served.build === runningBuild ? null : served);
    };
    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, [runningBuild, fetchBuild]);

  if (!available || available.build === dismissed) return null;
  return (
    <div className="update-banner-container">
      <div className="update-banner" role="status">
        <span className="update-banner__text">{updateMessage(available)}</span>
        <button type="button" className="update-banner__reload" onClick={() => window.location.reload()}>
          <FaSyncAlt size={12} /> Reload
        </button>
        <button type="button" className="update-banner__dismiss" aria-label="Dismiss until the next update"
          title="Dismiss until the next update" onClick={() => setDismissed(available.build)}>
          <FaTimes size={12} />
        </button>
      </div>
    </div>
  );
};

export default UpdateBanner;
